// ============================================================================
// remote/remoteFiles.ts — Ф3: типы и утилиты файлов устройства (host).
// Контракт сообщений: plans/remote-protocol-contract.md §6.
// ============================================================================

export interface HostFsEntry {
  name: string;
  is_dir: boolean;
  size: number;
  modified: string;
}

export interface FileListResult {
  path: string;
  parent: string;
  entries: HostFsEntry[];
}

export interface FileGetResult {
  name: string;
  size: number;
  data: string; // base64
}

/** base64 → байты (для скачивания файла). */
export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** байты → base64 (для загрузки файла). */
export function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    bin += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(bin);
}

/** Человекочитаемый размер. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

/** Скачать base64-файл браузером (viewer). */
export function downloadB64File(name: string, b64: string): void {
  const bytes = b64ToBytes(b64);
  const blob = new Blob([bytes.buffer as ArrayBuffer]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

type SaveFilePicker = (opts?: { suggestedName?: string }) => Promise<FileSystemFileHandle>;

/** Сохранить файл, полученный с удалённого ПК (viewer).
 *  Chromium: диалог выбора папки/имени (File System Access) — файл пишется
 *  напрямую, без «Загрузок». API недоступен → обычное скачивание.
 *  Отмена пользователем → null (НЕ скачиваем повторно). */
export async function saveRemoteFile(name: string, b64: string): Promise<'picker' | 'download' | null> {
  const bytes = b64ToBytes(b64);
  const win = window as Window & { showSaveFilePicker?: SaveFilePicker };
  if (typeof win.showSaveFilePicker === 'function') {
    try {
      const handle = await win.showSaveFilePicker({ suggestedName: name || 'file' });
      const writable = await handle.createWritable();
      await writable.write(new Blob([bytes.buffer as ArrayBuffer]));
      await writable.close();
      return 'picker';
    } catch (e) {
      if (e instanceof DOMException && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
        return null; // отказ/отмена — повторно не скачиваем
      }
      downloadB64File(name, b64);
      return 'download';
    }
  }
  downloadB64File(name, b64);
  return 'download';
}
